import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  TrendingUp,
  Package,
  FolderOpen,
  Users,
  DollarSign,
  Activity,
  Calendar,
  AlertCircle,
  Send,
  Clock,
  CheckCircle,
  XCircle,
  FileText,
  CreditCard,
  AlertTriangle
} from 'lucide-react';
import { dashboardAPI, projectsAPI, equipmentAPI, invoicesAPI, emailAPI } from '../services/api';

const Dashboard = () => {
  const navigate = useNavigate();
  const [dashboardStats, setDashboardStats] = useState(null);
  const [upcomingProjects, setUpcomingProjects] = useState([]);
  const [lowStockEquipment, setLowStockEquipment] = useState([]);
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [sentInvoices, setSentInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, projectsRes, equipmentRes, invoicesRes, sentInvoicesRes] = await Promise.all([
        dashboardAPI.getStats(),
        projectsAPI.getAll(),
        equipmentAPI.getAll(),
        invoicesAPI.getAll({ status: 'pending' }),
        emailAPI.getSentInvoices(5)
      ]);

      setDashboardStats(statsRes.data);

      const upcoming = projectsRes.data
        .filter(p => new Date(p.startDate) > new Date())
        .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
        .slice(0, 5);
      setUpcomingProjects(upcoming);

      const lowStock = equipmentRes.data.filter(e => e.available <= 2);
      setLowStockEquipment(lowStock);

      const pending = invoicesRes.data.filter(i => i.status === 'pending' || i.status === 'overdue');
      setPendingInvoices(pending);

      setSentInvoices(sentInvoicesRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Zojuist';
    if (diffMins < 60) return `${diffMins} min geleden`;
    if (diffHours < 24) return `${diffHours} uur geleden`;
    return `${diffDays} dagen geleden`;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'quote': 'Offerte',
      'pending': 'In Afwachting',
      'confirmed': 'Bevestigd',
      'in-progress': 'In Uitvoering',
      'completed': 'Afgerond'
    };
    return labels[status] || status;
  };

  if (loading || !dashboardStats) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Dashboard laden...</p>
        </div>
      </div>
    );
  }

  const mainStats = [
    {
      title: 'Betaald Deze Maand',
      value: `€${(dashboardStats.paidThisMonth || 0).toLocaleString('nl-NL', {minimumFractionDigits: 2})}`,
      subtitle: `Stripe: €${(dashboardStats.stripePaymentsThisMonth || 0).toLocaleString('nl-NL')}`,
      icon: DollarSign,
      color: 'from-green-500 to-green-600'
    },
    {
      title: 'Openstaand',
      value: `€${(dashboardStats.totalOutstanding || 0).toLocaleString('nl-NL', {minimumFractionDigits: 2})}`,
      subtitle: `${dashboardStats.pendingInvoices || 0} facturen`,
      icon: Clock,
      color: dashboardStats.totalOutstanding > 0 ? 'from-orange-500 to-orange-600' : 'from-gray-400 to-gray-500'
    },
    {
      title: 'Actieve Projecten',
      value: dashboardStats.activeProjects,
      subtitle: `${dashboardStats.autoInvoicePending || 0} auto-facturen wachtend`,
      icon: FolderOpen,
      color: 'from-purple-500 to-purple-600'
    },
    {
      title: 'Apparatuur',
      value: `${dashboardStats.availableEquipment}/${dashboardStats.totalEquipment}`,
      subtitle: `${dashboardStats.utilizationRate}% in gebruik`,
      icon: Package,
      color: 'from-blue-500 to-blue-600'
    }
  ];

  const quoteStats = [
    {
      title: 'Openstaande Offertes',
      value: dashboardStats.pendingQuotes || 0,
      color: 'text-blue-600',
      bg: 'bg-blue-50'
    },
    {
      title: 'Bijna Verlopen',
      value: dashboardStats.expiringQuotes || 0,
      color: 'text-orange-600',
      bg: 'bg-orange-50',
      alert: dashboardStats.expiringQuotes > 0
    },
    {
      title: 'Ondertekend',
      value: dashboardStats.signedQuotes || 0,
      color: 'text-green-600',
      bg: 'bg-green-50'
    },
    {
      title: 'Verlopen',
      value: dashboardStats.expiredQuotes || 0,
      color: 'text-gray-600',
      bg: 'bg-gray-50'
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500 mt-1">Welkom terug! Hier is wat er vandaag gebeurt.</p>
        </div>
        <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg" onClick={() => navigate('/calendar')}>
          <Calendar className="w-4 h-4 mr-2" />
          Bekijk Agenda
        </Button>
      </div>

      {/* Hoofdstatistieken */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {mainStats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                    <p className="text-2xl font-bold text-gray-900 mt-2">{stat.value}</p>
                    <p className="text-sm text-gray-500 mt-1">{stat.subtitle}</p>
                  </div>
                  <div className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-lg flex items-center justify-center shadow-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Offerte Statistieken */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50 py-4">
          <CardTitle className="flex items-center text-lg">
            <FileText className="w-5 h-5 mr-2 text-blue-600" />
            Offerte Overzicht
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {quoteStats.map((stat, index) => (
              <div key={index} className={`${stat.bg} rounded-lg p-4 ${stat.alert ? 'ring-2 ring-orange-400' : ''}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">{stat.title}</p>
                    <p className={`text-2xl font-bold ${stat.color} mt-1`}>{stat.value}</p>
                  </div>
                  {stat.alert && <AlertTriangle className="w-5 h-5 text-orange-500" />}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Aankomende Projecten */}
        <Card className="shadow-lg border-0">
          <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-purple-50">
            <CardTitle className="flex items-center text-lg">
              <FolderOpen className="w-5 h-5 mr-2 text-blue-600" />
              Aankomende Projecten
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              {upcomingProjects.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Geen aankomende projecten</p>
              ) : (
                upcomingProjects.map((project) => (
                  <div key={project.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">{project.name}</h4>
                      <p className="text-sm text-gray-600 mt-1">{project.customer}</p>
                      <div className="flex items-center space-x-4 mt-2">
                        <span className="text-xs text-gray-500">
                          {new Date(project.startDate).toLocaleDateString('nl-NL')}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          project.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                          project.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {getStatusLabel(project.status)}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-gray-900">€{(project.totalValue || project.totalPrice || 0).toLocaleString()}</p>
                      <p className="text-xs text-gray-500 mt-1">{project.equipmentCount || 0} items</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Verstuurde Facturen */}
        <Card className="shadow-lg border-0">
          <CardHeader className="border-b bg-gradient-to-r from-green-50 to-blue-50">
            <CardTitle className="flex items-center text-lg">
              <Send className="w-5 h-5 mr-2 text-green-600" />
              Recent Verstuurde Documenten
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              {sentInvoices.length === 0 ? (
                <div className="text-center py-8">
                  <Send className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">Nog geen documenten verstuurd</p>
                </div>
              ) : (
                sentInvoices.map((invoice, idx) => (
                  <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200">
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 ${invoice.type === 'quote' ? 'bg-blue-100' : 'bg-green-100'} rounded-full flex items-center justify-center`}>
                        {invoice.type === 'quote' ? (
                          <FileText className="w-4 h-4 text-blue-600" />
                        ) : (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        )}
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{invoice.invoice_number}</h4>
                        <p className="text-sm text-gray-600">{invoice.customer_name}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-gray-900">€{invoice.amount?.toLocaleString()}</p>
                      <p className="text-xs text-gray-500 mt-1">{formatTimeAgo(invoice.sent_at)}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Waarschuwingen en Info */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="shadow-lg border-0">
          <CardHeader className="border-b bg-gradient-to-r from-red-50 to-yellow-50">
            <CardTitle className="flex items-center text-lg">
              <AlertCircle className="w-5 h-5 mr-2 text-red-600" />
              Meldingen & Waarschuwingen
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              {dashboardStats.overdueInvoices > 0 && (
                <div className="p-4 bg-red-50 border-l-4 border-red-500 rounded">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 mr-3" />
                    <div>
                      <h4 className="font-semibold text-red-900">Achterstallige Facturen</h4>
                      <p className="text-sm text-red-700 mt-1">
                        {dashboardStats.overdueInvoices} facturen zijn achterstallig
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {dashboardStats.expiringQuotes > 0 && (
                <div className="p-4 bg-orange-50 border-l-4 border-orange-500 rounded">
                  <div className="flex items-start">
                    <AlertTriangle className="w-5 h-5 text-orange-500 mt-0.5 mr-3" />
                    <div>
                      <h4 className="font-semibold text-orange-900">Offertes Verlopen Binnenkort</h4>
                      <p className="text-sm text-orange-700 mt-1">
                        {dashboardStats.expiringQuotes} offertes verlopen binnen 3 dagen
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded">
                <div className="flex items-start">
                  <Package className="w-5 h-5 text-yellow-500 mt-0.5 mr-3" />
                  <div>
                    <h4 className="font-semibold text-yellow-900">Lage Voorraad</h4>
                    <p className="text-sm text-yellow-700 mt-1">
                      {lowStockEquipment.length} items hebben lage voorraad
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
                <div className="flex items-start">
                  <Users className="w-5 h-5 text-blue-500 mt-0.5 mr-3" />
                  <div>
                    <h4 className="font-semibold text-blue-900">Team Status</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      {dashboardStats.crewMembers} personeelsleden | {dashboardStats.activeCustomers} klanten
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="shadow-lg border-0">
          <CardHeader className="border-b bg-gradient-to-r from-purple-50 to-blue-50">
            <CardTitle className="flex items-center text-lg">
              <Activity className="w-5 h-5 mr-2 text-purple-600" />
              Snelle Acties
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-2 gap-4">
              <Button 
                variant="outline" 
                className="h-20 flex flex-col items-center justify-center hover:bg-blue-50 hover:border-blue-300"
                onClick={() => navigate('/projects')}
              >
                <FolderOpen className="w-6 h-6 mb-2 text-blue-600" />
                <span>Nieuw Project</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-20 flex flex-col items-center justify-center hover:bg-green-50 hover:border-green-300"
                onClick={() => navigate('/invoices')}
              >
                <FileText className="w-6 h-6 mb-2 text-green-600" />
                <span>Facturen</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-20 flex flex-col items-center justify-center hover:bg-purple-50 hover:border-purple-300"
                onClick={() => navigate('/equipment')}
              >
                <Package className="w-6 h-6 mb-2 text-purple-600" />
                <span>Apparatuur</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-20 flex flex-col items-center justify-center hover:bg-orange-50 hover:border-orange-300"
                onClick={() => navigate('/customers')}
              >
                <Users className="w-6 h-6 mb-2 text-orange-600" />
                <span>Klanten</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
